<script setup>
import SignInCard from '@/components/SignInCard.vue';
import { getUser, getUserAsync, refreshUsage } from '@/lib/user';
import { computed, ref } from 'vue';
import { cn } from '@/lib/utils';

const props = defineProps({
  class: String,
  show: Boolean,
  loginType: String,
});

const loginType = ref(props.loginType);
const container = ref(null);
const showEmail = ref(false);
const showPhone = ref(false);
const emit = defineEmits(['update:show', 'after-auth']);

const showLoginModal = computed({
  get: () => props.show,
  set: (v) => emit('update:show', v),
});

const modalTitle = computed(() => {
  if (showEmail.value) {
    return 'create.continueWithEmail';
  } else if (showPhone.value) {
    return 'create.continueWithPhone';
  }
  return 'create.loginMessage';
});

function afterAuth(token) {
  getUser();
  getUserAsync();
  refreshUsage();
  emit('after-auth', token);
}

function scrollToBottom() {
  if (container.value) {
    const scrollHeight = container.value.scrollHeight;
    container.value.scrollTo({
      top: scrollHeight,
      behavior: 'smooth',
    });
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="showLoginModal"
      :class="
        cn(
          'fixed h-screen inset-0 bg-black/30 flex items-center justify-center p-4 z-[9999]',
          props.class
        )
      "
      @click="showLoginModal = false"
      @wheel.stop
    >
      <div
        ref="container"
        class="bg-white/90 backdrop-blur-xl relative rounded-[32px] px-6 md:px-8 pt-12 pb-8 max-w-md w-full max-h-[86vh] shadow-lg overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-track-transparent scrollbar-thumb-black/10 hover:scrollbar-thumb-black/20"
        @click.stop
      >
        <div class="absolute top-3 right-3">
          <button
            class="group flex items-center justify-center rounded-full transition-all duration-200 hover:bg-black/5"
            style="width: 36px; height: 36px"
            @click="showLoginModal = false"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-black/40 group-hover:text-black/60 transition-colors duration-200"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Logo Container -->
        <div
          class="mx-auto mb-4 flex items-center justify-center"
          style="
            width: 64px;
            height: 64px;
            background-color: rgba(0, 0, 0, 0.8);
            border-radius: 20px;
          "
        >
          <svg
            width="32"
            height="52"
            viewBox="0 0 46 52"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M37.4648 19.4521C37.5497 19.3416 37.6931 19.2918 37.8281 19.3291C40.2663 20.0081 42.3011 21.4547 43.6514 23.8389C49.0988 33.4582 42.78 44.3769 29.4609 49.6816C15.4996 55.2418 0.735361 49.0757 0.404297 48.9062C0.207538 48.8047 0.0943971 48.5572 0.120117 48.3643C0.151872 48.1285 4.49624 39.399 11.3809 30.9072C11.5043 30.7542 11.748 30.804 11.8213 30.9854C11.9216 31.2322 12.0375 31.479 12.1699 31.7246C14.6661 36.34 23.0656 34.785 28.1582 30.2158C32.3011 26.9833 36.6679 20.5088 37.4648 19.4541V19.4521ZM16.1455 2.31836C30.1085 -3.24255 44.8744 2.9244 45.2021 3.09375C45.5185 3.25836 45.5355 3.47756 45.4365 3.76562C45.3925 3.84457 39.187 14.957 34.6289 20.6133C34.5891 20.6634 34.5328 20.733 34.4814 20.7959C34.4069 20.8859 34.2818 20.9169 34.1738 20.8682C33.9539 20.7704 33.5786 20.6023 33.2764 20.4814C30.7893 19.4848 25.617 17.9381 20.3818 19.499C15.0912 21.077 11.5179 24.6026 11.3623 28.3604C11.3558 28.5068 11.2981 28.6492 11.1992 28.752C11.1002 28.8548 11.0027 28.9593 10.9062 29.0635C9.81572 30.2453 8.81207 31.4044 7.89258 32.5283C7.82183 32.6141 7.70645 32.6512 7.59863 32.6191C5.24016 31.9196 3.27195 30.4861 1.95508 28.1611C-3.49244 18.5418 2.82635 7.62313 16.1455 2.31836Z"
              fill="white"
            />
          </svg>
        </div>

        <h2 class="mx-auto text-xl font-normal text-black/80 text-center mb-6">
          {{ $t(modalTitle) }}
        </h2>

        <SignInCard
          v-model:loginType="loginType"
          v-model:show-email="showEmail"
          v-model:show-phone="showPhone"
          class="border-0 shadow-none p-0"
          :after-auth="afterAuth"
          @scrollToBottom="scrollToBottom"
        />
      </div>
    </div>
  </Teleport>
</template>
